type PayloadRecord = Record<string, unknown>;

const hasOwn = (data: PayloadRecord, key: string) =>
  Object.prototype.hasOwnProperty.call(data, key);

const assignIfPresent = (
  source: PayloadRecord,
  target: PayloadRecord,
  sourceKey: string,
  targetKey: string,
) => {
  if (hasOwn(source, sourceKey)) {
    target[targetKey] = source[sourceKey];
  }
};

const buildPayload = (
  data: PayloadRecord,
  mappings: Array<[sourceKey: string, targetKey: string]>,
) => {
  const payload: PayloadRecord = {};
  for (const [sourceKey, targetKey] of mappings) {
    assignIfPresent(data, payload, sourceKey, targetKey);
  }
  return payload;
};

const replaceEmptyStringWithNull = (payload: PayloadRecord, key: string) => {
  if (payload[key] === "") {
    payload[key] = null;
  }
};

const commonKeys = ["matterId", "dateModified"] as const;

export const serializeMedicalTreatmentPayload = (data: PayloadRecord) =>
  buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "billNotApplicable",
      "description",
      "letterOfProtection",
      "lien",
      "nextSteps",
      "patientAccountNumber",
      "planNotApplicable",
      "portalDetails",
      "portalLink",
      "procedures",
      "recommendations",
      "recordNotApplicable",
      "soap",
      "status",
      "type",
    ].map((key) => [key, key]),
  );

export const serializeMedicalTreatmentAppointmentPayload = (
  data: PayloadRecord,
) =>
  buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentId",
      "billNotApplicable",
      "date",
      "dateEnd",
      "description",
      "multiday",
      "outcome",
      "missedReason",
      "recordNotApplicable",
      "time",
      "type",
    ].map((key) => [key, key]),
  );

export const serializeMedicalTreatmentAppointmentConnectionPayload = (
  data: PayloadRecord,
) =>
  buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentAppointmentId",
      "medicalTreatmentBillId",
      "medicalTreatmentPlanId",
      "medicalTreatmentRecordId",
      "medicalTreatmentId",
    ].map((key) => [key, key]),
  );

export const serializeMedicalTreatmentBillPayload = (data: PayloadRecord) => {
  const payload = buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentId",
      "adjustedBill",
      "clientPaid",
      "description",
      "fileNumber",
      "goalAmount",
      "insurancePaid",
      "letterOfProtection",
      "lien",
      "medpayPipPaid",
      "negotiatedAmount",
      "originalBill",
      "recordLabel",
      "recordReceivedDate",
      "recordRejectedDate",
      "recordRejectedReason",
      "recordRequestDate",
      "recordStatus",
      "reductionFeeAgreement",
      "remainingBalance",
      "status",
    ].map((key) => [key, key]),
  );

  replaceEmptyStringWithNull(payload, "recordLabel");
  return payload;
};

export const serializeMedicalTreatmentRecordPayload = (data: PayloadRecord) => {
  const payload = buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentId",
      "name",
      "receivedDate",
      "rejectedDate",
      "rejectedReason",
      "requestDate",
      "status",
    ].map((key) => [key, key]),
  );

  replaceEmptyStringWithNull(payload, "name");
  return payload;
};

export const serializeMedicalTreatmentPlanPayload = (data: PayloadRecord) =>
  buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentId",
      "date",
      "description",
      "status",
    ].map((key) => [key, key]),
  );

export const serializeMedicalTreatmentBalanceConfirmationPayload = (
  data: PayloadRecord,
) =>
  buildPayload(
    data,
    [
      ...commonKeys,
      "id",
      "medicalTreatmentId",
      "amount",
      "confirmed",
      "date",
    ].map((key) => [key, key]),
  );
